import React, { useState, useEffect } from "react";
import { Sparkles, X, Maximize2, Loader2, Image as ImageIcon } from "lucide-react";
import { COMPANY_INFO } from "../data";
import Sidebar from "../components/Sidebar";

const CATEGORIES = [
  { slug: "all", label: "Vis alle" },
  { slug: "vinduespudsning-privat", label: "Vinduespudsning Privat" },
  { slug: "vinduespudsning-erhverv", label: "Vinduespudsning Erhverv" },
  { slug: "solcellerens", label: "Solcellerens" },
  { slug: "algerens-tag", label: "Algerens Tag" },
  { slug: "algerens-fliser", label: "Algerens Fliser" },
  { slug: "campingvogne", label: "Campingvogne" },
];

export default function Gallery() {
  const [galleryItems, setGalleryItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [activeCategory, setActiveCategory] = useState("all");
  const [loading, setLoading] = useState(true);
  const [lightboxImage, setLightboxImage] = useState(null);

  // Fetch all gallery items from CMS
  useEffect(() => {
    async function fetchGallery() {
      try {
        const response = await fetch("https://api.dev.auxo.dk/public/content/gallery");
        if (response.ok) {
          const data = await response.json();
          setGalleryItems(data);
          setFilteredItems(data);
        }
      } catch (error) {
        console.error("Fejl ved hentning af galleri:", error);
      } finally {
        setLoading(false);
      }
    }
    fetchGallery();
  }, []);

  // Filter items when category changes
  useEffect(() => {
    if (activeCategory === "all") {
      setFilteredItems(galleryItems);
    } else {
      setFilteredItems(galleryItems.filter((item) => item.category === activeCategory));
    }
  }, [activeCategory, galleryItems]);

  return (
    <div className="pt-[72px] sm:pt-[128px]">
      {/* Hero Section */}
      <section className="relative bg-slate-950 py-24 sm:py-32 overflow-hidden text-center">
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/assets/66f352d5-c4b9-4e8b-8d46-eb7bb9ac1d49/0cf23f6e-19fb-4e7f-9a92-823688f27365.jpg"
            alt="Galleri"
            className="w-full h-full object-cover opacity-25"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/90 via-slate-950/75 to-slate-950" />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold bg-sky-500/10 text-sky-400 border border-sky-500/20 mb-6 uppercase tracking-wider">
            Mine Resultater
          </span>
          <h1 className="text-4xl sm:text-5xl font-extrabold text-white tracking-tight uppercase">
            Billedgalleri
          </h1>
          <p className="text-lg text-slate-300 mt-4 max-w-2xl mx-auto leading-relaxed">
            Gå på opdagelse i mit arbejde. Her kan du se før/efter-billeder og eksempler på mine professionelle ydelser udført på hele Fyn.
          </p>
        </div>
      </section>

      {/* Gallery Grid & Filter Section with Sidebar */}
      <section className="py-20 bg-white min-h-[600px]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
            
            {/* Left Column: Content (lg:col-span-12 since sidebar is removed on Gallery) */}
            <div className="lg:col-span-12 space-y-12">
              
              {/* Category Filter Bar */}
              <div className="flex flex-wrap justify-start gap-2.5">
                {CATEGORIES.map((cat) => (
                  <button
                    key={cat.slug}
                    onClick={() => setActiveCategory(cat.slug)}
                    className={`px-4 py-2 rounded-md text-xs font-bold uppercase tracking-wider transition-all cursor-pointer ${
                      activeCategory === cat.slug
                        ? "bg-sky-500 text-white shadow-sm"
                        : "bg-slate-50 text-slate-600 hover:bg-slate-100"
                    }`}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>

              {/* Gallery Items */}
              {loading ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                  {[1, 2, 3, 4, 5, 6].map((n) => (
                    <div
                      key={n}
                      className="bg-slate-50 rounded-md h-64 animate-pulse border border-slate-100"
                    />
                  ))}
                </div>
              ) : filteredItems.length === 0 ? (
                <div className="text-center py-20 bg-slate-50 rounded-md border border-slate-100 max-w-xl mx-auto">
                  <ImageIcon className="h-12 w-12 text-slate-400 mx-auto mb-4" />
                  <h3 className="text-lg font-bold text-slate-900 mb-1 uppercase tracking-wide">Ingen billeder fundet</h3>
                  <p className="text-slate-500 text-sm">
                    Der er i øjeblikket ingen billeder i denne kategori. Prøv en anden kategori.
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                  {filteredItems.map((item) => (
                    <div
                      key={item.id}
                      className="bg-white rounded-md overflow-hidden border border-slate-100 shadow-sm group relative cursor-pointer hover:shadow-md transition-all duration-300"
                      onClick={() => setLightboxImage(item.image[0])}
                    >
                      <div className="relative h-56 sm:h-64 overflow-hidden">
                        <img
                          src={item.image[0]}
                          alt={item.title.da}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                        <div className="absolute inset-0 bg-slate-950/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center z-10">
                          <div className="bg-white/90 backdrop-blur-sm p-3 rounded-md text-slate-900 shadow-lg transform translate-y-2 group-hover:translate-y-0 transition-transform">
                            <Maximize2 className="h-5 w-5" />
                          </div>
                        </div>
                      </div>
                      <div className="p-4">
                        <span className="text-[10px] font-bold text-sky-500 uppercase tracking-wider bg-sky-50 px-2 py-0.5 rounded-md">
                          {CATEGORIES.find((c) => c.slug === item.category)?.label || item.category}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

            </div>

          </div>
        </div>
      </section>

      {/* Lightbox Modal */}
      {lightboxImage && (
        <div
          className="fixed inset-0 bg-slate-950/95 z-50 flex items-center justify-center p-4 animate-in fade-in duration-200"
          onClick={() => setLightboxImage(null)}
        >
          <button
            className="absolute top-6 right-6 text-white bg-white/10 hover:bg-white/20 p-2 rounded-md transition-colors"
            onClick={() => setLightboxImage(null)}
          >
            <X className="h-6 w-6" />
          </button>
          <img
            src={lightboxImage}
            alt="Stor visning"
            className="max-w-full max-h-[90vh] object-contain rounded-md shadow-2xl animate-in zoom-in-95 duration-200"
          />
        </div>
      )}
    </div>
  );
}
